import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgForm } from '@angular/forms';
import { makePostalCodeMask } from '@app/functions';
import { Address } from '@app/models/common/address.model';
import { Address as AutocompleteAddress } from '@app/modules/applicant-tracker/interfaces/address.interface';
import { PlatformModule } from '@app/platform.module';
import { StateService } from '@app/services/state.service';
import { TranslateModule } from '@ngx-translate/core';

/**
 * AddressComponent provides you with an easy (hopefully!) way of handling
 * an address in a form.
 *
 * AddressComponent uses ngModel syntax.
 *
 * Example usage:
 *
 *             must have a name    binds to member variable     allows for autocomplete
 * <app-address name="address" [(ngModel)]="employeeAddress" [showAutocomplete]="true"></app-address>
 *
 */
@Component({
    imports: [CommonModule, FormsModule, PlatformModule, TranslateModule],
    selector: 'app-address',
    standalone: true,
    styleUrls: ['./address.style.scss'],
    templateUrl: './address.template.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AddressComponent), multi: true }],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class AddressComponent implements ControlValueAccessor {
    /**
     * showAutocomplete shows an input that will autocomplete the address
     */
    @Input() showAutocomplete = false;

    /**
     * autocompleteLabel allows you to override the default label
     * with a translatable string.
     */
    @Input() autocompleteLabel: string | null = null;

    onTouched?: (event: Event) => void;
    onChange?: (address: Address) => void;

    postalCodeMask = makePostalCodeMask();

    private _address: Address = new Address();

    constructor(private stateService: StateService) {}

    set address(address: Address) {
        this._address = address;
        this.onChange?.(address);
    }

    get address(): Address {
        return this._address;
    }

    async handleAddressChange(address: AutocompleteAddress): Promise<void> {
        // the interface makes everything optional
        // but if we don't even have a country, why bother with autocomplete?
        if (!address.country) {
            return;
        }

        // The AddressInterface does not provide the short code (eg. ON for Ontario) for provinces/states
        // so we get the short code from the backend.
        // We require the short code because our state picker only responds to short codes, even though it displays long names.
        try {
            this._address.province =
                (await this.stateService.getProvinceShortCode(address.country, address.region ?? '')) ?? '';
        } catch (error) {
            this._address.province = '';
        }

        this._address.country = address.country ?? '';
        this._address.city = address.city ?? '';
        this._address.postalCode = address.postalCode ?? '';
        this._address.addressLine1 = address.line1 ?? '';
        this._address.addressLine2 = address.line2 ?? '';

        this.onChange?.(this._address);
    }

    writeValue(address: Address): void {
        this._address = address;
    }

    registerOnChange(fn: (address: Address) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: (event: Event) => void): void {
        this.onTouched = fn;
    }
}
